import { ref, reactive, computed, watch, onBeforeUnmount } from 'vue'
import _ from 'lodash'
import MediaflowService from '@/services/MediaflowService'
import { sameTransform } from './sceneGeometry'
import { formatClockTime } from './mediaflowMeta'

/** Draft bucket for production-level graphics (they are not tied to a flow). */
const OVERLAYS = '__overlays'
const TEMP_PREFIX = 'draft-'
const UNDO_LIMIT = 50

/**
 * What a draft holds (per flow; the graphics bucket uses `pending` and `live`):
 * - pending: { [mediaId]: transform }
 * - adds:    { [tempId]: { payload, show, showTouched } } — created on Apply
 * - removes: { [mediaId]: true }
 * - edits:   { [mediaId]: settings patch } (Edit source)
 * - show:    { [mediaId]: boolean } — target visibility
 * - order:   [mediaId | tempId] | null — target layer order, top first
 * - live:    { [overlayId]: boolean } — graphics bucket: target on / off air
 */
const emptyState = () => ({ pending: {}, adds: {}, removes: {}, edits: {}, show: {}, order: null, live: {} })
const draftState = (d) => _.cloneDeep({
	pending: d.pending, adds: d.adds, removes: d.removes, edits: d.edits, show: d.show, order: d.order, live: d.live,
})
const setDraftState = (d, state) => { Object.assign(d, emptyState(), _.cloneDeep(state || {})) }
const size = (obj) => (obj ? Object.keys(obj).length : 0)
const hasStructural = (d) => !!d && (size(d.adds) + size(d.removes) + size(d.edits) + size(d.show) > 0 || !!d.order)
/** A pending add and its position count as one change. */
const changeCount = (d) => {
	if (!d) return 0
	const moved = Object.keys(d.pending || {}).filter((id) => !d.adds?.[id]).length
	return moved + size(d.adds) + size(d.removes) + size(d.edits) + size(d.show) + (d.order ? 1 : 0) + size(d.live)
}
const isTempId = (id) => typeof id === 'string' && id.startsWith(TEMP_PREFIX)
const visibleOf = (media) => media?.show !== false

/** The media list a flow has with its draft applied (server list when there is none). */
const draftedMedia = (flow, d) => {
	const base = Array.isArray(flow?.media) ? flow.media : []
	if (!hasStructural(d)) return base
	let list = base.filter((m) => !d.removes[m.id]).map((m) => {
		const edit = d.edits[m.id]
		const show = d.show[m.id]
		if (!edit && show === undefined) return m
		return { ...m, ...(edit || {}), ...(show !== undefined ? { show } : {}) }
	})
	// The server inserts each created source at the front (top layer), in the order the adds are applied.
	const added = Object.entries(d.adds).map(([id, add]) => ({ volume: 100, muted: false, audioOffset: 0, transform: null, ...add.payload, id, show: add.show !== false }))
	list = [...added.reverse(), ...list]
	if (d.order) {
		const rank = new Map(d.order.map((id, i) => [id, i]))
		list = list
			.map((m, i) => ({ m, r: rank.has(m.id) ? rank.get(m.id) : d.order.length + i }))
			.sort((a, b) => a.r - b.r)
			.map((x) => x.m)
	}
	return list
}

/**
 * Editing / draft / "Send to live" logic shared by the workspace and the
 * pop-out window. Every write still goes through the existing endpoints:
 * `updateFlowMedia(..., { transform })`, `/transition` and `switchScene`, plus
 * the new-engine overlay (`/overlays`) and break (`/cue`) calls.
 *
 * Rules:
 * - edited flow NOT on air: edits autosave (debounced).
 * - edited flow IS on air (the Program scene, live or not): edits are a local
 *   draft until "Apply to live", unless "Edit on air" is on — then each drop
 *   saves at once. Program only ever shows the saved state. The draft covers
 *   layout and the scene's structure (add / remove / reorder / show-hide /
 *   source settings); audio (volume, mute, sync) is a mixer and stays live.
 * - a flow that already has a structural draft keeps collecting edits in it
 *   (Preview shows the draft, so its indices and ids are the draft's).
 * - a graphic that is on air follows the same draft rule; one that is off
 *   air autosaves.
 * - Windows on the same production talk over a BroadcastChannel so the
 *   pop-out and the main tab show the same selection / draft / switch.
 */
export function useMediaflowDesk({ mediaflow, store, notify, channelName, sessionActive, detailPending = null }) {
	const editingFlowId = ref(null)
	const selectedMediaId = ref(null)
	const liveEdit = ref(false)
	// Transition used by "Send to live" (recorded via the existing /transition endpoint).
	const transitionType = ref('Fade')
	const transitionDuration = ref(300)
	const applying = ref(false)
	const sending = ref(false)
	const takingOverlayId = ref(null)
	const cueing = ref(false)
	// key (flowId | OVERLAYS) → { pending: { [mediaId]: transform }, undo: [], redo: [] }
	const drafts = reactive({})

	// Saved scene order (`order`, set by Move up / down); flows without one keep their place at the end.
	const flows = computed(() => {
		const list = Array.isArray(mediaflow.value?.flows) ? mediaflow.value.flows : []
		if (!list.some((f) => Number.isFinite(f?.order))) return list
		const rank = (f) => (Number.isFinite(f?.order) ? f.order : Number.MAX_SAFE_INTEGER)
		return list.map((f, i) => ({ f, i })).sort((a, b) => rank(a.f) - rank(b.f) || a.i - b.i).map((x) => x.f)
	})
	const layouts = computed(() => flows.value.filter((f) => f.kind !== 'break'))
	const breaks = computed(() => flows.value.filter((f) => f.kind === 'break'))
	const overlays = computed(() => (Array.isArray(mediaflow.value?.overlays) ? mediaflow.value.overlays : []))
	// Seeded from the list cache, the detail (with `capabilities`) may still be
	// on its way: no engine lock until it has arrived.
	const capabilities = computed(() => {
		const c = mediaflow.value?.capabilities
		if (!c && detailPending?.value) return { overlays: true, cueBreaks: true }
		return { overlays: !!c?.overlays, cueBreaks: !!c?.cueBreaks }
	})
	const cue = computed(() => mediaflow.value?.cue || null)
	const editingFlow = computed(() => flows.value.find((f) => f._id === editingFlowId.value) || null)
	const onAirFlow = computed(() => flows.value.find((f) => f.active) || null)
	const editingIsOnAir = computed(() => !!editingFlow.value && !!onAirFlow.value && editingFlow.value._id === onAirFlow.value._id)
	/** Edits to the edited flow need Apply (draft) rather than autosave. */
	const draftMode = computed(() => editingIsOnAir.value && !liveEdit.value)

	// ---------------------------------------------------------------- overlays as layers
	const overlayForMedia = (mediaId) => overlays.value.find((o) => o.media?.id === mediaId) || null
	const editingOverlayId = ref(null)
	const editingOverlay = computed(() => overlays.value.find((o) => o._id === editingOverlayId.value) || null)
	/** A graphic on Program is drafted like the on-air flow. */
	const overlayDraftMode = (overlay) => !!overlay?.live && !liveEdit.value
	/** While live (Edit on air off), switching a graphic on / off waits for Apply too. */
	const takesDrafted = computed(() => !!sessionActive?.value && !liveEdit.value)
	/** Staged on / off for a graphic (undefined when none). */
	const stagedLive = (overlay) => {
		const staged = overlay ? drafts[OVERLAYS]?.live?.[overlay._id] : undefined
		return typeof staged === 'boolean' ? staged : undefined
	}
	/** On / off as it will be after Apply (what Preview and the switch show). */
	const overlayTarget = (overlay) => {
		const staged = stagedLive(overlay)
		return staged === undefined ? !!overlay?.live : staged
	}
	/** 'on' / 'off' when a staged take differs from Program, else null. */
	const overlayPending = (overlay) => {
		const staged = stagedLive(overlay)
		return staged === undefined ? null : (staged ? 'on' : 'off')
	}
	// A staged take that Program already matches (applied elsewhere, or the
	// graphic is gone) is no longer a change.
	watch(overlays, (list) => {
		const d = drafts[OVERLAYS]
		if (!d || !size(d.live)) return
		let changed = false
		Object.entries(d.live).forEach(([id, target]) => {
			const o = list.find((x) => x._id === id)
			if (!o || !!o.live === target) { delete d.live[id]; changed = true }
		})
		if (changed) postDraft(OVERLAYS)
	})
	/** Graphics as Preview draws them: staged takes applied, flagged `pendingLive`. */
	const previewOverlays = computed(() => overlays.value.map((o) => {
		const staged = stagedLive(o)
		return staged === undefined ? o : { ...o, live: staged, pendingLive: true }
	}))

	const draftFor = (key) => {
		if (!key) return null
		if (!drafts[key]) drafts[key] = { ...emptyState(), undo: [], redo: [] }
		return drafts[key]
	}
	const draft = computed(() => (editingFlowId.value ? draftFor(editingFlowId.value) : null))
	const overlayDraft = computed(() => draftFor(OVERLAYS))
	const pendingCount = computed(() => changeCount(draft.value) + changeCount(overlayDraft.value))
	const buckets = () => [draft.value, overlayDraft.value].filter(Boolean)
	const canUndo = computed(() => buckets().some((d) => d.undo.length > 0))
	const canRedo = computed(() => buckets().some((d) => d.redo.length > 0))
	const hasOnAirDraft = computed(() => {
		const id = onAirFlow.value?._id
		return (!!id && changeCount(drafts[id]) > 0) || changeCount(overlayDraft.value) > 0
	})
	/**
	 * Structural edits to the scene in Preview are staged (no API call): it is on
	 * air without "Edit on air", or it already carries a structural draft.
	 */
	const stagesEdits = computed(() => !!editingFlow.value && (draftMode.value || hasStructural(drafts[editingFlowId.value])))
	/** The scene in Preview as it will be after Apply. Program keeps using the store. */
	const previewFlow = computed(() => {
		const flow = editingFlow.value
		if (!flow) return null
		const d = drafts[flow._id]
		return hasStructural(d) ? { ...flow, media: draftedMedia(flow, d) } : flow
	})
	/** Every flow as it will be after Apply (staged adds / removes included). */
	const draftedFlows = computed(() => flows.value.map((flow) => {
		const d = drafts[flow._id]
		return hasStructural(d) ? { ...flow, media: draftedMedia(flow, d) } : flow
	}))
	/** 'new' for a staged add, 'changed' for staged settings / visibility / position, else null. */
	const mediaDraftState = (mediaId) => {
		const d = editingFlowId.value ? drafts[editingFlowId.value] : null
		if (!d || !mediaId) return null
		if (d.adds[mediaId]) return 'new'
		return d.edits[mediaId] || d.show[mediaId] !== undefined || d.pending[mediaId] ? 'changed' : null
	}
	const postDraft = (key) => {
		const d = drafts[key]
		if (d) post({ type: 'draft', key, state: draftState(d) })
	}

	/** The transform the canvas should draw for a media item (draft wins). */
	const effectiveTransform = (media) => {
		if (!media) return null
		if (overlayForMedia(media.id)) {
			const d = drafts[OVERLAYS]
			if (d && d.pending[media.id]) return d.pending[media.id]
			return media.transform || null
		}
		const d = editingFlowId.value ? drafts[editingFlowId.value] : null
		if (d && d.pending[media.id]) return d.pending[media.id]
		return media.transform || null
	}

	// ---------------------------------------------------------------- cross-window sync
	let channel = null
	const post = (msg) => { try { channel?.postMessage({ ...msg, from: instanceId }) } catch (e) { /* ignore */ } }
	const instanceId = Math.random().toString(36).slice(2)
	const refreshFromServer = () => {
		if (mediaflow.value?._id) store.dispatch('Mediaflows/getMediaflow', mediaflow.value._id).catch(() => {})
	}
	if (channelName && typeof BroadcastChannel !== 'undefined') {
		channel = new BroadcastChannel(channelName)
		channel.onmessage = (event) => {
			const msg = event.data || {}
			if (msg.from === instanceId) return
			switch (msg.type) {
				case 'select':
					editingFlowId.value = msg.flowId || null
					selectedMediaId.value = null
					editingOverlayId.value = null
					break
				case 'select-media':
					selectedMediaId.value = msg.mediaId || null
					editingOverlayId.value = msg.overlayId || null
					break
				case 'draft': {
					const d = draftFor(msg.key || msg.flowId)
					if (d) setDraftState(d, msg.state || { ...draftState(d), pending: msg.pending || {} })
					break
				}
				case 'live-edit':
					liveEdit.value = !!msg.value
					break
				case 'cue':
					if (mediaflow.value?._id) store.dispatch('Mediaflows/setCue', { mediaflowId: mediaflow.value._id, cue: msg.cue || null })
					refreshFromServer()
					break
				case 'refresh':
				case 'take':
					refreshFromServer()
					break
				default:
					break
			}
		}
	}
	onBeforeUnmount(() => { channel?.close(); channel = null })

	// ---------------------------------------------------------------- transition
	// Cut / Fade and the fade duration survive a reload, per production; other
	// windows on the same production pick the change up through `storage`.
	const transitionKey = () => (mediaflow.value?._id ? `castr-mf-transition:${mediaflow.value._id}` : '')
	const applyStoredTransition = (raw) => {
		let saved = null
		try { saved = raw ? JSON.parse(raw) : null } catch (e) { saved = null }
		if (!saved || typeof saved !== 'object') return
		if (saved.type === 'Cut' || saved.type === 'Fade') transitionType.value = saved.type
		const ms = parseInt(saved.duration, 10)
		if (Number.isFinite(ms) && ms > 0) transitionDuration.value = ms
	}
	watch(() => mediaflow.value?._id, () => {
		const key = transitionKey()
		if (!key) return
		try { applyStoredTransition(localStorage.getItem(key)) } catch (e) { /* private mode */ }
	}, { immediate: true })
	watch([transitionType, transitionDuration], ([type, duration]) => {
		const key = transitionKey()
		if (!key) return
		try { localStorage.setItem(key, JSON.stringify({ type, duration: parseInt(duration, 10) || 300 })) } catch (e) { /* private mode */ }
	})
	const onTransitionStorage = (event) => {
		if (event.key && event.key === transitionKey()) applyStoredTransition(event.newValue)
	}
	if (typeof window !== 'undefined') window.addEventListener('storage', onTransitionStorage)
	onBeforeUnmount(() => { if (typeof window !== 'undefined') window.removeEventListener('storage', onTransitionStorage) })

	// ---------------------------------------------------------------- selection
	// Preview survives a reload: the last previewed scene per production.
	const previewKey = () => (mediaflow.value?._id ? `castr-mf-preview:${mediaflow.value._id}` : '')
	watch(editingFlowId, (id) => {
		const key = previewKey()
		if (!key || !id) return
		try { localStorage.setItem(key, id) } catch (e) { /* private mode */ }
	})
	/** Scene Preview opens on: the remembered one, else the on-air scene (or the one a break returns to), else the first. */
	const restorePreviewId = () => {
		const list = layouts.value
		let saved = null
		try { saved = previewKey() ? localStorage.getItem(previewKey()) : null } catch (e) { saved = null }
		const pick = list.find((f) => f._id === saved)
			|| list.find((f) => f.active)
			|| list.find((f) => f._id === cue.value?.returnFlowId)
			|| list[0]
		return pick?._id || null
	}

	const selectFlow = (flowId) => {
		if (editingFlowId.value === flowId) return
		editingFlowId.value = flowId
		selectedMediaId.value = null
		editingOverlayId.value = null
		liveEdit.value = false
		post({ type: 'select', flowId })
	}
	const selectMedia = (mediaId) => {
		selectedMediaId.value = mediaId || null
		editingOverlayId.value = overlayForMedia(mediaId)?._id || null
		post({ type: 'select-media', mediaId: selectedMediaId.value, overlayId: editingOverlayId.value })
	}
	/** Open a graphic on the Preview canvas for positioning. */
	const selectOverlay = (overlayId) => {
		const overlay = overlays.value.find((o) => o._id === overlayId) || null
		editingOverlayId.value = overlay?._id || null
		selectedMediaId.value = overlay?.media?.id || null
		post({ type: 'select-media', mediaId: selectedMediaId.value, overlayId: editingOverlayId.value })
	}
	const setLiveEdit = (value) => {
		liveEdit.value = !!value
		post({ type: 'live-edit', value: liveEdit.value })
	}
	// Live edit is an explicit choice per opened flow.
	watch(editingFlowId, () => { liveEdit.value = false })

	// ---------------------------------------------------------------- saving
	const saveTransform = async (flowId, mediaId, transform) => {
		const mfId = mediaflow.value?._id
		if (!mfId || !mediaId) return
		if (flowId === OVERLAYS) {
			const overlay = overlayForMedia(mediaId)
			if (!overlay) return
			await store.dispatch('Mediaflows/updateOverlay', {
				mediaflowId: mfId,
				overlayId: overlay._id,
				data: { media: { ...overlay.media, transform } },
			})
			return
		}
		if (!flowId) return
		await MediaflowService.updateFlowMedia(mfId, flowId, mediaId, { transform })
		store.dispatch('Mediaflows/updateFlowMedia', { mediaflowId: mfId, flowId, mediaId, media: { transform } })
	}

	/** Optimistic store update so the canvas and the sources list stay in sync. */
	const reflectInStore = (key, mediaId, transform) => {
		const mfId = mediaflow.value?._id
		if (!mfId) return
		if (key === OVERLAYS) {
			const overlay = overlayForMedia(mediaId)
			if (overlay) store.dispatch('Mediaflows/patchOverlay', { mediaflowId: mfId, overlayId: overlay._id, overlay: { media: { transform } } })
			return
		}
		store.dispatch('Mediaflows/updateFlowMedia', { mediaflowId: mfId, flowId: key, mediaId, media: { transform } })
	}

	// ---------------------------------------------------------------- optimistic writes
	// A full refresh (getMediaflow) replaces the whole production. One that was
	// requested before a save landed still carries the old transform and would
	// throw the source back. Each local write stays authoritative until the
	// server has acknowledged that exact transform and a snapshot agrees (or a
	// short grace period after the ack has passed).
	const ACK_GRACE_MS = 6000
	const writes = new Map()
	const writeKey = (key, mediaId) => `${key}:${mediaId}`
	const serverTransformOf = (snapshot, key, mediaId) => {
		if (!snapshot) return undefined
		if (key === OVERLAYS) return (snapshot.overlays || []).find((o) => o.media?.id === mediaId)?.media?.transform
		const flow = (snapshot.flows || []).find((f) => f._id === key)
		return flow ? (flow.media || []).find((m) => m.id === mediaId)?.transform : undefined
	}
	const mediaName = (key, mediaId) => {
		if (key === OVERLAYS) return overlayForMedia(mediaId)?.name || 'the graphic'
		return (flows.value.find((f) => f._id === key)?.media || []).find((m) => m.id === mediaId)?.name || 'the source'
	}
	const recordWrite = (key, mediaId, transform) => {
		const wk = writeKey(key, mediaId)
		const prev = writes.get(wk)
		// What to roll back to: the last acknowledged state, not an intermediate local one.
		const before = prev ? (prev.ackedAt ? prev.transform : prev.before) : serverTransformOf(mediaflow.value, key, mediaId) ?? null
		clearTimeout(prev?.timer)
		writes.set(wk, { key, mediaId, transform, before, ackedAt: 0, timer: null })
	}
	const ackWrite = (key, mediaId, transform) => {
		const entry = writes.get(writeKey(key, mediaId))
		if (!entry || !sameTransform(entry.transform, transform)) return
		entry.ackedAt = Date.now()
		entry.timer = setTimeout(() => { if (writes.get(writeKey(key, mediaId)) === entry) writes.delete(writeKey(key, mediaId)) }, ACK_GRACE_MS)
	}
	const failWrite = (key, mediaId, transform) => {
		const wk = writeKey(key, mediaId)
		const entry = writes.get(wk)
		// A newer local write is already on its way: that one decides.
		if (!entry || !sameTransform(entry.transform, transform)) return
		writes.delete(wk)
		reflectInStore(key, mediaId, entry.before)
		notify?.({ group: 'error', text: `Couldn’t move ${mediaName(key, mediaId)}. It’s back where it was.` })
	}
	// `sync`, so a stale snapshot is corrected before it is ever painted.
	watch(() => mediaflow.value, (next, prev) => {
		if (!next || next === prev || !writes.size) return
		writes.forEach((entry, wk) => {
			const server = serverTransformOf(next, entry.key, entry.mediaId)
			if (server === undefined) { writes.delete(wk); return }
			if (sameTransform(server, entry.transform)) {
				if (entry.ackedAt) { clearTimeout(entry.timer); writes.delete(wk) }
				return
			}
			if (entry.ackedAt && Date.now() - entry.ackedAt > ACK_GRACE_MS) { writes.delete(wk); return }
			reflectInStore(entry.key, entry.mediaId, entry.transform)
		})
	}, { flush: 'sync' })

	const savers = new Map()
	const dragOrigin = new Map()
	const saverFor = (key, mediaId, kind) => {
		const k = `${kind}:${key}:${mediaId}`
		if (!savers.has(k)) {
			const run = async (transform) => {
				try {
					await saveTransform(key, mediaId, transform)
					ackWrite(key, mediaId, transform)
				} catch (err) {
					console.error(err)
					failWrite(key, mediaId, transform)
				}
			}
			savers.set(k, kind === 'live'
				? _.throttle(run, 150, { leading: true, trailing: true })
				: _.debounce(run, 400))
		}
		return savers.get(k)
	}

	const pushUndo = (d, step) => {
		d.undo.push({ ...step, at: Date.now() })
		if (d.undo.length > UNDO_LIMIT) d.undo.shift()
		d.redo = []
	}
	const recordStep = (d, key, mediaId, before, after) => pushUndo(d, { key, mediaId, before, after })

	/**
	 * Called by the canvas / templates / fine controls. `final` is true on
	 * pointer-up (or for one-shot edits) and false while dragging.
	 */
	const updateTransform = (media, transform, { final = true } = {}) => {
		if (!media) return
		const overlay = overlayForMedia(media.id)
		const key = overlay ? OVERLAYS : editingFlowId.value
		if (!key) return
		const payload = {
			positionX: Math.round(Number(transform.positionX) || 0),
			positionY: Math.round(Number(transform.positionY) || 0),
			scaleX: Math.round((Number(transform.scaleX) || 0) * 100) / 100,
			scaleY: Math.round((Number(transform.scaleY) || 0) * 100) / 100,
		}
		const drafted = overlay ? overlayDraftMode(overlay) : stagesEdits.value

		if (drafted) {
			const d = draftFor(key)
			// One undo step per gesture: remember the state before the first move.
			if (!dragOrigin.has(media.id)) dragOrigin.set(media.id, d.pending[media.id] ? { ...d.pending[media.id] } : null)
			if (sameTransform(payload, media.transform)) delete d.pending[media.id]
			else d.pending[media.id] = payload
			if (final) {
				const before = dragOrigin.get(media.id)
				dragOrigin.delete(media.id)
				const after = d.pending[media.id] ? { ...payload } : null
				if (!sameTransform(before || media.transform, after || media.transform)) recordStep(d, key, media.id, before, after)
				postDraft(key)
			}
			return
		}

		// A draft left from before "Edit on air" must not keep drawing this
		// source at its old spot: the saved position is what counts now.
		const d = drafts[key]
		if (d?.pending[media.id]) {
			delete d.pending[media.id]
			postDraft(key)
		}
		recordWrite(key, media.id, payload)
		reflectInStore(key, media.id, payload)
		// Intermediate steps (arrow-key bursts) only move the picture; the final one saves.
		if (!final) return
		const streamLive = overlay ? (overlay.live && liveEdit.value) : (editingIsOnAir.value && liveEdit.value)
		const saver = saverFor(key, media.id, streamLive ? 'live' : 'auto')
		saver(payload)
		saver.flush()
	}

	const latestBucket = (stack) => {
		let best = null
		buckets().forEach((d) => {
			const top = d[stack][d[stack].length - 1]
			if (top && (!best || top.at > best.top.at)) best = { d, top }
		})
		return best
	}
	const undo = () => {
		const hit = latestBucket('undo')
		if (!hit) return
		const { d } = hit
		const step = d.undo.pop()
		if (step.kind === 'state') setDraftState(d, step.before)
		else if (step.before) d.pending[step.mediaId] = step.before
		else delete d.pending[step.mediaId]
		d.redo.push(step)
		postDraft(step.key)
	}
	const redo = () => {
		const hit = latestBucket('redo')
		if (!hit) return
		const { d } = hit
		const step = d.redo.pop()
		if (step.kind === 'state') setDraftState(d, step.after)
		else if (step.after) d.pending[step.mediaId] = step.after
		else delete d.pending[step.mediaId]
		d.undo.push(step)
		postDraft(step.key)
	}

	// ---------------------------------------------------------------- staged structure
	const serverMediaOf = (flowId, mediaId) => (flows.value.find((f) => f._id === flowId)?.media || []).find((m) => m.id === mediaId) || null
	/** Drop an order that only restates the natural one. */
	const normalizeOrder = (flowId, d) => {
		if (!d.order) return
		const flow = flows.value.find((f) => f._id === flowId)
		const natural = draftedMedia(flow, { ...d, order: null }).map((m) => m.id)
		const order = d.order.filter((id) => natural.includes(id))
		const full = [...order, ...natural.filter((id) => !order.includes(id))]
		d.order = _.isEqual(full, natural) ? null : full
	}
	/** One undoable structural step on the scene in Preview. */
	const stage = (mutate) => {
		const key = editingFlowId.value
		if (!key || !editingFlow.value) return false
		const d = draftFor(key)
		const before = draftState(d)
		mutate(d)
		normalizeOrder(key, d)
		const after = draftState(d)
		if (_.isEqual(before, after)) return false
		pushUndo(d, { key, kind: 'state', before, after })
		postDraft(key)
		return true
	}
	const previewIds = () => (previewFlow.value?.media || []).map((m) => m.id)

	const stageReorder = (fromIndex, toIndex) => stage((d) => {
		const ids = previewIds()
		if (fromIndex < 0 || fromIndex >= ids.length || toIndex < 0 || toIndex >= ids.length) return
		ids.splice(toIndex, 0, ids.splice(fromIndex, 1)[0])
		d.order = ids
	})
	const stageToggleShow = (mediaId) => stage((d) => {
		if (d.adds[mediaId]) {
			d.adds[mediaId].show = d.adds[mediaId].show === false
			d.adds[mediaId].showTouched = true
			return
		}
		const base = serverMediaOf(editingFlowId.value, mediaId)
		if (!base || d.removes[mediaId]) return
		const target = !(d.show[mediaId] !== undefined ? d.show[mediaId] : visibleOf(base))
		if (target === visibleOf(base)) delete d.show[mediaId]
		else d.show[mediaId] = target
	})
	const stageRemove = (mediaId) => {
		const changed = stage((d) => {
			delete d.pending[mediaId]
			delete d.edits[mediaId]
			delete d.show[mediaId]
			if (d.adds[mediaId]) delete d.adds[mediaId]
			else if (serverMediaOf(editingFlowId.value, mediaId)) d.removes[mediaId] = true
			if (d.order) d.order = d.order.filter((id) => id !== mediaId)
		})
		if (changed && selectedMediaId.value === mediaId) selectMedia(null)
		return changed
	}
	/** Stage a new source; it draws in Preview with the data it will be created with. */
	const stageAdd = (payload) => {
		const tempId = `${TEMP_PREFIX}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`
		stage((d) => {
			d.adds[tempId] = { payload: _.cloneDeep(payload || {}), show: true, showTouched: false }
			if (d.order) d.order.unshift(tempId)
		})
		return tempId
	}
	/** Stage Edit source settings; only fields that differ from the saved source are kept. */
	const stageEdit = (mediaId, patch) => stage((d) => {
		if (d.adds[mediaId]) {
			Object.assign(d.adds[mediaId].payload, _.cloneDeep(patch || {}))
			return
		}
		const base = serverMediaOf(editingFlowId.value, mediaId)
		if (!base || d.removes[mediaId]) return
		const next = { ...(d.edits[mediaId] || {}) }
		Object.entries(patch || {}).forEach(([k, v]) => {
			if (v === undefined || _.isEqual(base[k], v)) delete next[k]
			else next[k] = _.cloneDeep(v)
		})
		if (Object.keys(next).length) d.edits[mediaId] = next
		else delete d.edits[mediaId]
	})

	const clearDraft = (key) => {
		const d = key ? drafts[key] : null
		if (!d) return
		const mfId = mediaflow.value?._id
		// Images uploaded for sources that will never be created.
		Object.values(d.adds || {}).forEach((add) => {
			const imageId = add?.payload?.imageId
			if (imageId && mfId && key !== OVERLAYS) MediaflowService.deleteFlowImage(mfId, key, imageId).catch(() => {})
		})
		setDraftState(d, emptyState())
		d.undo = []
		d.redo = []
		postDraft(key)
	}
	const discardDraft = () => {
		if (editingFlowId.value) clearDraft(editingFlowId.value)
		clearDraft(OVERLAYS)
	}

	// ---------------------------------------------------------------- apply
	const mediaDefaults = (m) => ({ volume: 100, show: false, muted: false, audioOffset: 0, ...m })
	const replaceFlowMediaInStore = (flowId, list) => {
		const mfId = mediaflow.value?._id
		store.dispatch('Mediaflows/clearFlowMedia', { mediaflowId: mfId, flowId })
		list.forEach((m) => store.dispatch('Mediaflows/addFlowMedia', { mediaflowId: mfId, flowId, media: mediaDefaults(m) }))
	}
	const renameDraftId = (d, from, to) => {
		if (d.pending[from]) { d.pending[to] = d.pending[from]; delete d.pending[from] }
		if (d.order) d.order = d.order.map((id) => (id === from ? to : id))
	}
	class ApplyError extends Error {
		constructor(what, cause) {
			super(cause?.message || (typeof cause === 'string' ? cause : ''))
			this.what = what
		}
	}

	/**
	 * Sends the flow's structural draft in order: adds → removes → settings and
	 * visibility → reorder. Each op leaves the draft once the server took it, so
	 * a failure keeps exactly what is still to do. Returns the ops sent.
	 */
	const applyStructure = async (key, d) => {
		const mfId = mediaflow.value?._id
		const currentMedia = () => flows.value.find((f) => f._id === key)?.media || []
		let done = 0
		const run = async (what, fn) => {
			try {
				await fn()
			} catch (err) {
				throw err instanceof ApplyError ? err : new ApplyError(what, err)
			}
		}

		/* eslint-disable no-await-in-loop */
		for (const tempId of Object.keys(d.adds)) {
			const add = d.adds[tempId]
			const name = add.payload?.name || 'the source'
			await run(`add ${name}`, async () => {
				const known = new Set(currentMedia().map((m) => m.id))
				const res = await MediaflowService.createFlowMedia(mfId, key, add.payload)
				const list = Array.isArray(res) ? res : null
				const created = list ? [...list].reverse().find((m) => m?.id && !known.has(m.id)) : (res?.id ? res : null)
				if (list) replaceFlowMediaInStore(key, list)
				else if (created) store.dispatch('Mediaflows/addFlowMedia', { mediaflowId: mfId, flowId: key, media: mediaDefaults(created) })
				delete d.adds[tempId]
				done += 1
				if (!created) {
					delete d.pending[tempId]
					if (d.order) d.order = d.order.filter((id) => id !== tempId)
					return
				}
				renameDraftId(d, tempId, created.id)
				if (add.showTouched && visibleOf(created) !== (add.show !== false)) d.show[created.id] = add.show !== false
			})
		}

		for (const mediaId of Object.keys(d.removes)) {
			const name = serverMediaOf(key, mediaId)?.name || 'the source'
			await run(`remove ${name}`, async () => {
				const res = await store.dispatch('Mediaflows/deleteFlowMedia', { mediaflowId: mfId, flowId: key, mediaId })
				if (res && res.success === false) throw new ApplyError(`remove ${name}`, res.message || '')
				delete d.removes[mediaId]
				done += 1
			})
		}

		for (const mediaId of Object.keys(d.edits)) {
			const name = serverMediaOf(key, mediaId)?.name || 'the source'
			await run(`update ${name}`, async () => {
				const patch = d.edits[mediaId]
				if (serverMediaOf(key, mediaId)) {
					await MediaflowService.updateFlowMedia(mfId, key, mediaId, patch)
					store.dispatch('Mediaflows/updateFlowMedia', { mediaflowId: mfId, flowId: key, mediaId, media: patch })
				}
				delete d.edits[mediaId]
				done += 1
			})
		}

		for (const mediaId of Object.keys(d.show)) {
			const media = serverMediaOf(key, mediaId)
			const target = d.show[mediaId]
			await run(`${target ? 'show' : 'hide'} ${media?.name || 'the source'}`, async () => {
				// The endpoint flips: only call it when the server differs from the target.
				if (media && visibleOf(media) !== target) {
					await MediaflowService.toggleFlowMedia(mfId, key, mediaId)
					store.dispatch('Mediaflows/updateFlowMedia', { mediaflowId: mfId, flowId: key, mediaId, media: { show: target } })
				}
				delete d.show[mediaId]
				done += 1
			})
		}

		if (d.order) {
			await run('reorder the sources', async () => {
				const ids = currentMedia().map((m) => m.id)
				const wanted = d.order.filter((id) => ids.includes(id))
				const target = [...wanted, ...ids.filter((id) => !wanted.includes(id))]
				for (let i = 0; i < target.length; i += 1) {
					const from = ids.indexOf(target[i])
					if (from === i) continue
					await MediaflowService.reorderFlowMedia(mfId, key, from, i)
					store.dispatch('Mediaflows/reorderFlowMedia', { mediaflowId: mfId, flowId: key, fromIndex: from, toIndex: i })
					ids.splice(i, 0, ids.splice(from, 1)[0])
				}
				d.order = null
				done += 1
			})
		}
		/* eslint-enable no-await-in-loop */
		return done
	}

	/** Sends the draft of the scene in Preview and the graphics draft; stops at the first failure. */
	const applyDraft = async () => {
		if (applying.value) return false
		const key = editingFlowId.value
		const d = key ? draft.value : null
		const overlayBucket = overlayDraft.value
		const total = changeCount(d) + changeCount(overlayBucket)
		if (!total) return true
		applying.value = true
		let done = 0
		let failure = null
		try {
			if (d && hasStructural(d)) done += await applyStructure(key, d)
			const jobs = []
			if (d) Object.entries(d.pending).forEach(([mediaId, transform]) => jobs.push({ d, key, mediaId, transform }))
			Object.entries(overlayBucket.pending).forEach(([mediaId, transform]) => jobs.push({ d: overlayBucket, key: OVERLAYS, mediaId, transform }))
			for (const job of jobs) {
				if (isTempId(job.mediaId)) continue
				try {
					// eslint-disable-next-line no-await-in-loop
					await saveTransform(job.key, job.mediaId, job.transform)
				} catch (err) {
					throw new ApplyError(`move ${mediaName(job.key, job.mediaId)}`, err)
				}
				// The refresh below may race an older one: keep the applied spot.
				recordWrite(job.key, job.mediaId, job.transform)
				ackWrite(job.key, job.mediaId, job.transform)
				delete job.d.pending[job.mediaId]
				done += 1
			}
			// Graphics on / off last, so one that comes on air does so at its new spot.
			for (const [overlayId, target] of Object.entries(overlayBucket.live || {})) {
				const overlay = overlays.value.find((o) => o._id === overlayId)
				if (overlay && !!overlay.live !== target) {
					const what = `${target ? 'show' : 'hide'} ${overlay.name}`
					let res
					try {
						// eslint-disable-next-line no-await-in-loop
						res = await store.dispatch('Mediaflows/takeOverlay', { mediaflowId: mediaflow.value._id, overlayId, on: target, transition: overlay.transition })
					} catch (err) {
						throw new ApplyError(what, err)
					}
					if (res && res.success === false) throw new ApplyError(what, res.message || '')
					post({ type: 'take', overlayId })
				}
				delete overlayBucket.live[overlayId]
				done += 1
			}
		} catch (err) {
			console.error(err)
			failure = err instanceof ApplyError ? err : new ApplyError('apply the changes', err)
		}
		buckets().forEach((b) => { b.undo = []; b.redo = [] })
		applying.value = false
		if (d) postDraft(key)
		postDraft(OVERLAYS)
		refreshFromServer()
		post({ type: 'refresh' })
		if (failure) {
			const left = changeCount(d) + changeCount(overlayBucket)
			const reason = failure.message ? ` (${failure.message})` : ''
			const kept = left ? ` ${left} ${left === 1 ? 'change is' : 'changes are'} still a draft.` : ''
			notify?.({ group: 'error', text: `Couldn’t ${failure.what}${reason}.${done ? ` ${done} applied.` : ''}${kept}` })
			return false
		}
		notify?.({ group: 'success', text: `${done} ${done === 1 ? 'change' : 'changes'} applied${sessionActive.value ? ' to live' : ''}` })
		return true
	}

	// ---------------------------------------------------------------- draft survives a reload (this tab)
	const storageKey = () => (mediaflow.value?._id ? `castr-mf-drafts:${mediaflow.value._id}` : '')
	let restoredFor = null
	/** Keep only entries that still point at something that exists. */
	const pruneState = (key, state) => {
		const s = { ...emptyState(), ...(state || {}) }
		const ids = new Set(key === OVERLAYS
			? overlays.value.map((o) => o.media?.id)
			: (flows.value.find((f) => f._id === key)?.media || []).map((m) => m.id))
		Object.keys(s.adds || {}).forEach((id) => ids.add(id))
		const keep = (obj) => _.pickBy(obj || {}, (_v, id) => ids.has(id))
		const order = Array.isArray(s.order) ? s.order.filter((id) => ids.has(id)) : null
		const overlayIds = new Set(overlays.value.map((o) => o._id))
		const live = key === OVERLAYS ? _.pickBy(s.live || {}, (v, id) => overlayIds.has(id) && typeof v === 'boolean') : {}
		return { ...s, pending: keep(s.pending), removes: keep(s.removes), edits: keep(s.edits), show: keep(s.show), order: order?.length ? order : null, live }
	}
	watch(() => [mediaflow.value?._id, flows.value.length], () => {
		const mfId = mediaflow.value?._id
		if (!mfId || restoredFor === mfId || !flows.value.length) return
		restoredFor = mfId
		let saved = null
		try { saved = JSON.parse(sessionStorage.getItem(storageKey()) || 'null') } catch (e) { saved = null }
		if (!saved || typeof saved !== 'object') return
		Object.entries(saved).forEach(([key, state]) => {
			if (key !== OVERLAYS && !flows.value.some((f) => f._id === key)) return
			const d = draftFor(key)
			if (changeCount(d)) return
			setDraftState(d, pruneState(key, state))
			if (key !== OVERLAYS) normalizeOrder(key, d)
		})
	}, { immediate: true })
	watch(drafts, () => {
		const k = storageKey()
		if (!k || restoredFor !== mediaflow.value?._id) return
		const out = {}
		Object.entries(drafts).forEach(([key, d]) => { if (changeCount(d)) out[key] = draftState(d) })
		try {
			if (Object.keys(out).length) sessionStorage.setItem(k, JSON.stringify(out))
			else sessionStorage.removeItem(k)
		} catch (e) { /* private mode / quota */ }
	}, { deep: true })

	// ---------------------------------------------------------------- send to live
	/**
	 * Put a flow on air (defaults to the edited one). Before a session exists
	 * this only marks the flow active so Go live opens with it — the switch
	 * endpoint updates the active flags with or without an instance.
	 */
	const sendToLive = async (flowId = editingFlowId.value) => {
		const flow = flows.value.find((f) => f._id === flowId)
		const mfId = mediaflow.value?._id
		if (!flow || !mfId || sending.value) return false
		sending.value = true
		try {
			if (sessionActive.value) {
				try {
					await MediaflowService.transition(mfId, {
						transitionName: transitionType.value,
						duration: parseInt(transitionDuration.value, 10) || 300,
					})
				} catch (err) {
					// Transition settings are best-effort; the switch is what matters.
					console.error('transition settings failed', err)
				}
			}
			await store.dispatch('Mediaflows/switchFlow', { mediaflowId: mfId, flowId: flow._id })
			// A manual switch ends whatever break was running.
			if (cue.value) store.dispatch('Mediaflows/setCue', { mediaflowId: mfId, cue: null })
			post({ type: 'take', flowId: flow._id })
			notify?.({ group: 'success', text: sessionActive.value ? `${flow.name} is on air` : `${flow.name} will open when you go live` })
			return true
		} catch (err) {
			console.error('Failed to switch flow', err)
			notify?.({ group: 'error', text: err?.message || 'Could not switch scenes' })
			return false
		} finally {
			sending.value = false
		}
	}

	// ---------------------------------------------------------------- graphics take
	/**
	 * Switch a graphic on / off. While live (Edit on air off) the change is
	 * staged in the graphics draft and only reaches Program on Apply; offline
	 * it sets what the stream starts with, and with Edit on air it goes out now.
	 */
	const takeOverlay = async (overlay, on = !overlayTarget(overlay)) => {
		if (!overlay || !mediaflow.value?._id || !capabilities.value.overlays) return false
		if (takesDrafted.value) return stageOverlayTake(overlay, !!on)
		return sendOverlayTake(overlay, !!on)
	}
	/** One undoable step in the graphics draft: the graphic's target on / off. */
	const stageOverlayTake = (overlay, on) => {
		const d = draftFor(OVERLAYS)
		const before = draftState(d)
		if (on === !!overlay.live) delete d.live[overlay._id]
		else d.live[overlay._id] = on
		const after = draftState(d)
		if (_.isEqual(before, after)) return false
		pushUndo(d, { key: OVERLAYS, kind: 'state', before, after })
		postDraft(OVERLAYS)
		return true
	}
	/** Put a graphic on / off air now with its own transition. Optimistic, reverted on error. */
	const sendOverlayTake = async (overlay, on) => {
		const mfId = mediaflow.value?._id
		if (!overlay || !mfId || takingOverlayId.value) return false
		if (!capabilities.value.overlays) return false
		takingOverlayId.value = overlay._id
		const before = overlay.live
		const patch = (live) => store.dispatch('Mediaflows/patchOverlay', { mediaflowId: mfId, overlayId: overlay._id, overlay: { live } })
		patch(!!on)
		try {
			await store.dispatch('Mediaflows/takeOverlay', { mediaflowId: mfId, overlayId: overlay._id, on: !!on, transition: overlay.transition })
			post({ type: 'take', overlayId: overlay._id })
			return true
		} catch (err) {
			patch(before)
			console.error('Failed to take overlay', err)
			notify?.({ group: 'error', text: err?.message || `Could not ${on ? 'show' : 'hide'} ${overlay.name}` })
			return false
		} finally {
			takingOverlayId.value = null
		}
	}

	// ---------------------------------------------------------------- breaks
	/** Cue a break now: program switches to it and the server returns after its duration. */
	const cueBreak = async (flow, overrides = {}) => {
		const mfId = mediaflow.value?._id
		if (!flow || !mfId || cueing.value || !capabilities.value.cueBreaks) return false
		cueing.value = true
		try {
			const settings = flow.break || {}
			const data = {
				durationSec: overrides.durationSec ?? settings.durationSec ?? 30,
				overlays: overrides.overlays ?? settings.overlays ?? 'keep',
				transition: { type: transitionType.value, duration: parseInt(transitionDuration.value, 10) || 300 },
			}
			// `returnFlowId: null` = back to whatever was on air when the break started.
			const returnFlowId = overrides.returnFlowId !== undefined ? overrides.returnFlowId : settings.returnFlowId
			data.returnFlowId = returnFlowId || onAirFlow.value?._id || null
			await store.dispatch('Mediaflows/cueFlow', { mediaflowId: mfId, flowId: flow._id, data })
			post({ type: 'cue', cue: mediaflow.value?.cue || null })
			notify?.({ group: 'success', text: `${flow.name} on air for ${data.durationSec}s` })
			return true
		} catch (err) {
			console.error('Failed to cue break', err)
			notify?.({ group: 'error', text: err?.message || 'Could not cue the break' })
			return false
		} finally {
			cueing.value = false
		}
	}

	const endBreak = async () => {
		const mfId = mediaflow.value?._id
		if (!mfId || cueing.value || !cue.value) return false
		// A recurring automation break can't be cut short on the compositor.
		if (cue.value.source === 'automation' && cue.value.endable === false) {
			const at = cue.value.returnsAt ? formatClockTime(Date.parse(cue.value.returnsAt)) : ''
			notify?.({ group: 'info', text: `This break is part of a recurring schedule and returns on its own${at ? ` at ${at}` : ''}.` })
			return false
		}
		cueing.value = true
		try {
			await store.dispatch('Mediaflows/endCue', { mediaflowId: mfId })
			post({ type: 'cue', cue: null })
			notify?.({ group: 'success', text: 'Break ended' })
			return true
		} catch (err) {
			console.error('Failed to end break', err)
			notify?.({ group: 'error', text: err?.message || 'Could not end the break' })
			return false
		} finally {
			cueing.value = false
		}
	}

	return {
		OVERLAYS,
		flows,
		editingFlowId,
		editingFlow,
		onAirFlow,
		editingIsOnAir,
		draftMode,
		selectedMediaId,
		liveEdit,
		transitionType,
		transitionDuration,
		applying,
		sending,
		takingOverlayId,
		takesDrafted,
		overlayTarget,
		overlayPending,
		previewOverlays,
		cueing,
		drafts,
		draft,
		overlayDraft,
		pendingCount,
		canUndo,
		canRedo,
		hasOnAirDraft,
		stagesEdits,
		previewFlow,
		draftedFlows,
		mediaDraftState,
		isTempId,
		stageReorder,
		stageToggleShow,
		stageRemove,
		stageAdd,
		stageEdit,
		clearDraft,
		layouts,
		breaks,
		overlays,
		capabilities,
		cue,
		editingOverlayId,
		editingOverlay,
		overlayForMedia,
		overlayDraftMode,
		effectiveTransform,
		selectFlow,
		restorePreviewId,
		selectMedia,
		selectOverlay,
		setLiveEdit,
		updateTransform,
		undo,
		redo,
		applyDraft,
		discardDraft,
		sendToLive,
		takeOverlay,
		cueBreak,
		endBreak,
		post,
	}
}
